<template>
    <TkRightDrawer
        :model-value="modelValue"
        :title="isEdit ? 'Edit Te Kohinga Person' : 'Create Te Kohinga Person'"
        :subtitle="isEdit
            ? 'Update this Te Kohinga Person\'s details.'
            : 'Creates a Te Kohinga Person record that can be linked to a Paua client later.'"
        width="760px"
        @update:model-value="$emit('update:modelValue', $event)"
    >
        <q-form @submit.prevent="submit">
                <q-card-section>
                    <TkPersonFields v-model="draft" :dense="true" />
                    <q-separator class="q-mt-md q-mb-sm" />
                    <div class="row items-center q-mb-xs">
                        <p class="q-my-none"><strong>
                            Whānau Contact
                        </strong></p>
                        <q-space />
                        <q-btn
                            flat
                            dense
                            no-caps
                            color="primary"
                            icon="add"
                            label="Add whānau contact"
                            @click="addEmergencyContact"
                        />
                    </div>
                    <div class="text-caption text-grey-8 q-mb-sm">
                        Record the people to contact in an emergency.
                    </div>
                    <div
                        v-for="(contact, index) in draft.emergency_contacts"
                        :key="index"
                        class="row q-col-gutter-sm q-mb-sm items-start"
                    >
                        <div class="col-12 col-sm-4">
                            <q-input
                                v-model="contact.name"
                                outlined
                                dense
                                maxlength="255"
                                label="Name"
                            />
                        </div>
                        <div class="col-12 col-sm-3">
                            <q-input
                                v-model="contact.relationship"
                                outlined
                                dense
                                maxlength="100"
                                label="Relationship"
                            />
                        </div>
                        <div class="col">
                            <q-input
                                v-model="contact.phone"
                                outlined
                                dense
                                maxlength="50"
                                type="tel"
                                inputmode="tel"
                                label="Phone number"
                                hide-bottom-space
                                :rules="[v => {
                                    const value = String(v ?? '').trim().replace(/[()\s.-]/g, '');
                                    return value === '' || /^(\+?\d+)$/.test(value) || 'Please enter a valid phone number.';
                                }]"
                            />
                        </div>
                        <div class="col-auto">
                            <q-checkbox
                                v-model="contact.permission_to_contact"
                                dense
                                label="Permission to contact"
                                class="q-mt-xs"
                            />
                            <q-btn
                                flat
                                round
                                dense
                                color="negative"
                                icon="delete_outline"
                                aria-label="Remove emergency contact"
                                @click="removeEmergencyContact(index)"
                            />
                        </div>
                    </div>
                </q-card-section>
                <q-separator />
                <q-card-actions align="right">
                    <q-btn flat rounded no-caps color="negative" label="Cancel" v-close-popup />
                    <q-btn
                        flat rounded no-caps outline
                        color="primary"
                        :label="isEdit ? 'Save Changes' : 'Create Te Kohinga Person'"
                        type="submit"
                        :loading="saving"
                    />
                </q-card-actions>
        </q-form>
    </TkRightDrawer>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import { personPhoneNumbers } from "@/services/util/tkClients";
import TkPersonFields from "./TkPersonFields.vue";
import TkRightDrawer from "@/components/TkClients/TkRightDrawer.vue";

// Convert a DB date_of_birth value (Date object, ISO string, or null) to DD/MM/YYYY for the input.
const toDDMMYYYY = (value) => {
    if (!value) return "";
    if (value instanceof Date) {
        const dd = String(value.getDate()).padStart(2, "0");
        const mm = String(value.getMonth() + 1).padStart(2, "0");
        const yyyy = value.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }
    const iso = String(value).slice(0, 10); // YYYY-MM-DD
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : "";
};

// Convert DD/MM/YYYY input back to YYYY-MM-DD for the API, or null if blank/invalid.
const toISODate = (value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return null;
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : null;
};

const emptyEmergencyContact = () => ({
    name: "",
    relationship: "",
    phone: "",
    permission_to_contact: false,
});

const emergencyContactsFromPerson = (person) => {
    if (Array.isArray(person?.emergency_contacts) && person.emergency_contacts.length) {
        return person.emergency_contacts.map(contact => ({
            name: contact.name ?? "",
            relationship: contact.relationship ?? "",
            phone: contact.phone ?? "",
            permission_to_contact: contact.permission_to_contact === true,
        }));
    }
    const legacyContact = {
        name: person?.emergency_contact_name ?? "",
        relationship: person?.emergency_contact_relationship ?? "",
        phone: person?.emergency_contact_phone ?? "",
        permission_to_contact: false,
    };
    return legacyContact.name || legacyContact.relationship || legacyContact.phone
        ? [legacyContact]
        : [emptyEmergencyContact()];
};

const draftFromPerson = (person) => {
    const phoneNumbers = personPhoneNumbers(person).map(phone => ({
        phone_type: phone.phone_type,
        phone_number: phone.phone_number,
    }));
    return {
        first_name: person?.first_name ?? "",
        last_name: person?.last_name ?? "",
        preferred_name: person?.preferred_name ?? "",
        date_of_birth: toDDMMYYYY(person?.date_of_birth),
        gender_id: person?.gender_id ?? null,
        ethnicity_id: person?.ethnicity_id ?? null,
        iwi_id: person?.iwi_id ?? null,
        nhi: person?.nhi ?? "",
        phone: phoneNumbers[0]?.phone_number ?? "",
        phone_numbers: phoneNumbers,
        email: person?.email ?? "",
        address_line_1: person?.address_line_1 ?? "",
        suburb: person?.suburb ?? "",
        city: person?.city ?? "",
        postcode: person?.postcode ?? "",
        emergency_contacts: emergencyContactsFromPerson(person),
        aliases: Array.isArray(person?.aliases)
            ? person.aliases.map(alias => ({
                first_name: alias.first_name ?? "",
                last_name: alias.last_name ?? "",
                source: alias.source ?? "",
            }))
            : [],
    };
};

export default {
    name: "TkPersonDialog",
    components: { TkPersonFields, TkRightDrawer },
    emits: ["update:modelValue", "created", "updated"],
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        // When provided, the dialog operates in edit mode against this record.
        person: {
            type: [Object, null],
            default: null,
        },
        // Tags newly-created tk_people records with the screen/feature that created them.
        originService: {
            type: String,
            default: "dynamic_forms",
        },
    },
    data() {
        return {
            saving: false,
            draft: draftFromPerson(this.person),
        };
    },
    computed: {
        isEdit() {
            return !!this.person?.id;
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.draft = draftFromPerson(this.person);
            }
        },
    },
    methods: {
        addEmergencyContact() {
            this.draft.emergency_contacts.push(emptyEmergencyContact());
        },
        removeEmergencyContact(index) {
            this.draft.emergency_contacts.splice(index, 1);
        },
        async submit() {
            this.saving = true;
            try {
                const payload = {
                    first_name: this.draft.first_name?.trim() || null,
                    last_name: this.draft.last_name?.trim() || null,
                    preferred_name: this.draft.preferred_name?.trim() || null,
                    date_of_birth: toISODate(this.draft.date_of_birth),
                    gender_id: this.draft.gender_id || null,
                    ethnicity_id: this.draft.ethnicity_id || null,
                    iwi_id: this.draft.iwi_id || null,
                    nhi: this.draft.nhi?.trim() || null,
                    phone: this.draft.phone_numbers[0]?.phone_number?.trim() || null,
                    phone_numbers: this.draft.phone_numbers.map(phone => ({
                        phone_type: phone.phone_type?.trim() || null,
                        phone_number: phone.phone_number?.trim() || null,
                    })),
                    email: this.draft.email?.trim() || null,
                    address_line_1: this.draft.address_line_1?.trim() || null,
                    suburb: this.draft.suburb?.trim() || null,
                    city: this.draft.city?.trim() || null,
                    postcode: this.draft.postcode?.trim() || null,
                    emergency_contacts: this.draft.emergency_contacts
                        .map(contact => ({
                            name: contact.name?.trim() || null,
                            relationship: contact.relationship?.trim() || null,
                            phone: contact.phone?.trim() || null,
                            permission_to_contact: contact.permission_to_contact,
                        }))
                        .filter(contact => contact.name || contact.relationship || contact.phone),
                    aliases: this.draft.aliases.map(alias => ({
                        first_name: alias.first_name?.trim() || null,
                        last_name: alias.last_name?.trim() || null,
                        source: alias.source?.trim() || null,
                    })),
                };
                if (this.isEdit) {
                    const res = await api.tkPeople.updatePerson(this.person.id, payload);
                    this.$emit("updated", res.data);
                    this.$emit("update:modelValue", false);
                    notify.primary("Te Kohinga Person updated.", "top");
                } else {
                    const res = await api.tkPeople.createPerson({
                        origin_service: this.originService,
                        ...payload,
                    });
                    this.$emit("created", res.data);
                    this.$emit("update:modelValue", false);
                    notify.primary("Te Kohinga Person created.", "top");
                }
            } catch (e) {
                notify.error(
                    this.isEdit
                        ? "Failed to update Te Kohinga Person. Please try again."
                        : "Failed to create Te Kohinga Person. Please try again.",
                    "top",
                );
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>
